import {
  faCheck,
  faCircleExclamation,
  faInfoCircle,
  faWarning,
  faXmark,
} from '@awesome.me/kit-5452b20ec3/icons/classic/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

import styles from './CustomToastMessage.module.scss'

const toastTypeDisplay = {
  error: {
    icon: faCircleExclamation,
    color: 'var(--vip-color-text-error)',
    label: 'Error!',
  },
  success: {
    icon: faCheck,
    color: 'var(--vip-color-text-success)',
    label: 'Success!',
  },
  warning: {
    icon: faWarning,
    color: 'var(--vip-color-text-warning)',
    label: 'Warning!',
  },
  info: {
    icon: faInfoCircle,
    color: 'var(--vip-color-text-information)',
    label: 'Attention!',
  },
}

interface CustomToastMessageProps {
  children: React.ReactNode
  type: 'error' | 'success' | 'warning' | 'info'
  onClose: () => void
}

export const CustomToastMessage = ({ children, type = 'error', onClose }: CustomToastMessageProps) => {
  const icon = toastTypeDisplay[type].icon
  const color = toastTypeDisplay[type].color
  const label = toastTypeDisplay[type].label

  const TOAST_DURATION = 5000
  const [isPaused, setIsPaused] = useState(false)
  const [animateBar, setAnimateBar] = useState(false)

  useEffect(() => {
    if (isPaused) return

    // Start bar animation
    requestAnimationFrame(() => setAnimateBar(true))

    // Close toast after duration
    const timer = setTimeout(onClose, TOAST_DURATION)

    return () => clearTimeout(timer)
  }, [isPaused])

  return (
    <div
      className={classNames(styles['toast-container'], styles[type])}
      onMouseEnter={() => setIsPaused(true)}
    >
      {/* Toast timer bar */}
      <div
        style={{
          backgroundColor: color,
          height: '4px',
          width: isPaused ? '100%' : animateBar ? '100%' : '0%',
          position: 'absolute',
          bottom: 0,
          left: 0,
          transition: isPaused ? 'none' : `width ${TOAST_DURATION}ms linear`,
        }}
      />

      {/* Close button */}
      <div className={styles['toast-message-close']}>
        <FontAwesomeIcon icon={faXmark} onClick={onClose} />
      </div>

      <header className={styles['toast-message-header']}>
        <FontAwesomeIcon icon={icon} color={color} />
        <h3 className={styles['toast-message-title']} style={{ color: color }}>
          {label}
        </h3>
      </header>

      <div className={styles['toast-message-content']}>{children}</div>
    </div>
  )
}
